import * as vp from 'vopay';

import { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';

import vopayApi from '../../../services/api.config';
import Account from './Account';

import OnboardingUrlDialogue from './OnboardingUrlDialogue';
import useFetchPartnerOnboardingAppUrlInfo from '../../../hooks/useApi/useFetchPartnerOnboardingAppUrlInfo';
import { useModuleFeatureFlag } from '../../../providers/FeatureFlag.provider';

import type {
  AccountsHandlers,
  Balances as Balance,
  DialogueHandlerProps,
  OnboardingUrlDialogueHandlers,
} from '../types';

type AccountContainerProps = {
  api: vp.Api;
  handlers: AccountsHandlers;
};

type FC = FunctionComponent<AccountContainerProps>;

const AccountContainer: FC = ({ api, handlers }: AccountContainerProps) => {
  /*

    ▼ setup initial state for individual vopay account container ▼

  */

  const history = useHistory();

  const { canAccessOnboardingTokenPanel } = useModuleFeatureFlag('accounts');

  const query = new URLSearchParams(useLocation().search);
  const balance: Balance = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as vp.SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  const { accountId } = useParams<{ accountId: string }>();

  const [shouldGenerateOnboardingUrl, setGenerateOnboardingUrl] = useState(false);
  const [shouldFetchOnboardingUrl, setFetchOnboardingUrl] = useState(canAccessOnboardingTokenPanel);
  const [hasValidOnboardingUrl, setHasValidOnboardingUrl] = useState<boolean | null>(null);
  const [onBoardingLink, setOnboardingLink] = useState<string | null>(null);

  const [fetchOnboardingUrlError, setFetchOnboardingUrlError] = useState<string>('');
  const [showFetchOnboardingUrlErrorAlert, setFetchOnboardingUrlErrorAlert] = useState(false);

  const [generateOnboardingUrlError, setGenerateOnboardingUrlError] = useState<string>('');
  const [showGenerateOnboardingUrlErrorAlert, setGenerateOnboardingUrlErrorAlert] = useState(false);

  const [partnerOnboardingUrlInfo, fetchPartnerOnboardingUrlInfo, resetPartnerOnboardingUrlInfo] =
    useFetchPartnerOnboardingAppUrlInfo({});
  const [fetchOnboardingAppUrl, setFetchOnboardingAppUrl] = useState(false);
  const source = axios.CancelToken.source();

  const [hasNewOnboardingUrl, setHasNewOnboardingUrl] = useState(false);

  /*

    ▼ initial call and handling of account onboarding url ▼

  */

  useEffect(() => {
    if (balance.isActive) return () => {};
    if (!shouldFetchOnboardingUrl) return () => {};
    const loadData = () => {
      setFetchOnboardingAppUrl(true);
      fetchPartnerOnboardingUrlInfo({ VopayAccountID: accountId });
    };
    return loadData;
  }, [shouldFetchOnboardingUrl, hasNewOnboardingUrl]);

  useEffect(() => {
    const loadData = () => {
      if (fetchOnboardingAppUrl) {
        setHasNewOnboardingUrl(false);
        const { ErrorMessage, OnboardingURL } = partnerOnboardingUrlInfo;
        if (OnboardingURL) {
          setOnboardingLink(OnboardingURL);
          setHasValidOnboardingUrl(OnboardingURL !== '');
        }

        if (ErrorMessage) {
          if (ErrorMessage !== '') {
            setHasValidOnboardingUrl(false);
          } else {
            setFetchOnboardingUrlError(ErrorMessage);
            setFetchOnboardingUrlErrorAlert(true);
          }
        }
        resetPartnerOnboardingUrlInfo();
        setFetchOnboardingAppUrl(false);
      }
      return setFetchOnboardingUrl(false);
    };

    return loadData();
  }, [partnerOnboardingUrlInfo, hasNewOnboardingUrl]);

  /*

    ▼ handle the renewal of account onboarding url ▼

  */

  useEffect(() => {
    if (!shouldGenerateOnboardingUrl) return () => {};

    const renewOnboardingUrl = async () => {
      const { Success, ErrorMessage } = await api.fetchOnboardingAppUrl({ VopayAccountID: accountId }, source);

      if (Success) {
        setFetchOnboardingUrl(true);
        setHasValidOnboardingUrl(true);
        setHasNewOnboardingUrl(true);
      }

      if (ErrorMessage) {
        setGenerateOnboardingUrlError(ErrorMessage);
        setGenerateOnboardingUrlErrorAlert(true);
      }

      setGenerateOnboardingUrl(false);
    };

    void renewOnboardingUrl();

    return () => {
      source.cancel();
    };
  }, [shouldGenerateOnboardingUrl]);

  /*

    ▼ handlers to be passed as props ▼

  */

  const handleAccountOnboardingClick = () => {
    if (onBoardingLink) window.open(onBoardingLink);
  };

  const handleCopyClick = () => {
    if (onBoardingLink) void navigator?.clipboard?.writeText(onBoardingLink);
  };

  const handleRenewOnboardingUrlClick = () => {
    setHasValidOnboardingUrl(null);
    setGenerateOnboardingUrl(true);
  };

  const handleDialogue = (handlerProps: DialogueHandlerProps<OnboardingUrlDialogueHandlers>) => {
    const { type, state: dState, action = 'default', hasNavigation } = handlerProps;

    const dialogueHandlers: OnboardingUrlDialogueHandlers = {
      retrieval: {
        success: {
          default: () => {},
        },
        fail: {
          default: () => {
            setFetchOnboardingUrlErrorAlert(false);
            setFetchOnboardingUrlError('');
          },
        },
      },
      generation: {
        success: {
          default: () => {},
        },
        fail: {
          default: () => {
            setGenerateOnboardingUrlErrorAlert(false);
            setGenerateOnboardingUrlError('');
            setHasValidOnboardingUrl(false);
          },
        },
      },
    };

    const dialogueState = dialogueHandlers[type][dState];
    const handler = dialogueState[action] || dialogueState.default;

    handler();

    if (hasNavigation) history.push('/accounts');
  };

  const getAccount = useCallback(() => {
    const [accounts, setAccounts] = useState(
      <Account
        hasValidOnboardingUrl={hasValidOnboardingUrl}
        handlers={handlers}
        onActionClick={hasValidOnboardingUrl ? handleAccountOnboardingClick : handleRenewOnboardingUrlClick}
        onCopyClick={handleCopyClick}
        onBoardingLink={onBoardingLink}
      />,
    );

    useEffect(() => {
      setAccounts(
        <Account
          hasValidOnboardingUrl={hasValidOnboardingUrl}
          handlers={handlers}
          onActionClick={hasValidOnboardingUrl ? handleAccountOnboardingClick : handleRenewOnboardingUrlClick}
          onCopyClick={handleCopyClick}
          onBoardingLink={onBoardingLink}
        />,
      );
    }, [hasValidOnboardingUrl, onBoardingLink]);

    return accounts;
  }, [hasValidOnboardingUrl, onBoardingLink]);

  return (
    <div>
      {getAccount()}
      <OnboardingUrlDialogue
        fetchOnboardingUrl={{ fetchOnboardingUrlError, showFetchOnboardingUrlErrorAlert }}
        generateOnboardingUrl={{ generateOnboardingUrlError, showGenerateOnboardingUrlErrorAlert }}
        handleDialogue={handleDialogue}
      />
    </div>
  );
};

export default vopayApi.withApiComponent<AccountContainerProps>(AccountContainer);
